import Head from 'next/head'
import styled from 'styled-components'

import Bugsnag from '@bugsnag/js'
import Cookies from 'js-cookie'
import { toProductDataItem } from 'layout/navigation/search/utils'
import { serverSideConfigs } from 'localization'
import { NextPageContext } from 'next'
import ShrinkWrappedJordan1Bred from 'notFound/ShrinkWrappedJordan1Bred'
import { useCallback, useEffect, useState } from 'react'
import TrendingProducts from 'search/components/TrendingProducts'
import { ErrorPagesQA } from 'shared/dataAttributes'
import { STORAGE } from 'shared/enums/SitePreferences'
import { colors, fonts, media, styles } from 'shared/lib'
import { ProductDataItem } from 'shared/types/ConsumerSearch'
import { getServerPageProps } from 'shared/utils/getServerSideProps'
import { getConsumerSearchFilters } from 'shared/utils/search/cs-utils'

type ErrorStatusProps = {
  header: string
  subtitle: string
  title: string
}

const statusInfo = new Map<number, ErrorStatusProps>([
  [
    404,
    {
      title: '404 Not Found',
      header: '404 - Page Not Found',
      subtitle:
        'Sorry, we could not find this page. Please search again or navigate to the home page to find what you are looking for.',
    },
  ],
  [
    500,
    {
      title: '500 Internal Server Error',
      header: 'Something went wrong.',
      subtitle:
        "An error has occurred and we're working to fix it. Please try again in a few minutes",
    },
  ],
])

type ErrorProps = {
  statusCode?: number
}

const Error = ({ statusCode }: ErrorProps) => {
  const errorInfo = (statusCode && statusInfo.get(statusCode)) || statusInfo.get(404)!
  const { header, subtitle, title } = errorInfo

  const [consumerSearchTrendingSearch, setConsumerSearchTrendingSearch] = useState<
    ProductDataItem[]
  >([])

  const getConsumerSearchTrendingSearch = useCallback(async () => {
    const consumerSearchTrendingSearchQuery = getConsumerSearchFilters({
      searchState: {},
      cookies: {
        currency: Cookies.get(STORAGE.CURRENCY) as string,
        country: Cookies.get(STORAGE.COUNTRY) as string,
      },
      pageLimit: '8',
    })
    const url =
      '/api/product-search?' + new URLSearchParams(consumerSearchTrendingSearchQuery).toString()
    const { data } = await fetch(url, {
      method: 'GET',
    })
      .then(async (res) => {
        return res.json()
      })
      .catch(() => {
        return {
          data: {},
          error: 'error fetching data',
          total: 0,
        }
      })
    setConsumerSearchTrendingSearch(toProductDataItem(data?.productsList || []))
  }, [])

  // TODO: Fix assign - Exclude error page from experiment while a fix for SSR assign is found
  useEffect(() => {
    void getConsumerSearchTrendingSearch()
  }, [])

  return (
    <>
      <Head>
        <title>{title} | Flight Club</title>
      </Head>
      <TopBanner>
        <BannerMessage>
          <Header data-qa={ErrorPagesQA.ErrorPageSectionTitle}>{header}</Header>
          <Subtitle data-qa={ErrorPagesQA.ErrorPageSectionSubTitle}>{subtitle}</Subtitle>
        </BannerMessage>
        <SneakerImage />
      </TopBanner>
      <TrendingProducts hits={consumerSearchTrendingSearch} />
    </>
  )
}

Error.getInitialProps = async (context: NextPageContext) => {
  const { res, locale, err } = context

  if (err) {
    // Must be created within getInitialProps because this relies on runtime configs.  If invoked
    // outside of getInitialProps or componentDidMount, the project will try to create a bugsnag
    // client when building
    Bugsnag.notify(err)
  }

  const statusCode = res ? res.statusCode : err ? err.statusCode : 404

  return {
    props: {
      ...(await getServerPageProps(context)),
      ...(await serverSideConfigs(res, locale)),
      statusCode,
    },
  }
}

const TopBanner = styled.div`
  background-color: ${colors.FC2_WHITE};
  display: flex;
  justify-content: center;
  flex-wrap: wrap-reverse;
  padding: ${styles.mobilePageLayoutPadding};
`

const BannerMessage = styled.div`
  max-width: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 20px 0 40px;
`

const Header = styled.h1`
  ${fonts.HEADER_1};
  font-weight: 500;
`

const Subtitle = styled.h2`
  ${fonts.BODY_TEXT};
  font-weight: unset;
`

const SneakerImage = styled(ShrinkWrappedJordan1Bred)`
  ${media.large`
    margin: 50px 20px;
  `}
`

export default Error
